import React, { useCallback, useContext } from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import { useQueryClient } from '@gogame/query';
import { useScreenTitle } from '@gogame/screens';
import { Link, Tags } from '@gogame/components';

import { DIMENSION_CONCEPT } from 'scenes/Trackers/Tracker/constants';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';

import ConceptFamilyDetails from 'scenes/Gallery/GalleryPage/ConceptFamilies/ConceptFamilyDetails';
import ConceptDetails from 'scenes/Gallery/GalleryPage/Concepts/ConceptDetails';
import {
  SCOPE_CONCEPTS,
  TAB_COMMENTS,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { AttributeItem } from 'scenes/Gallery/components/AttributeList';
import ExpirationDate from 'scenes/Gallery/components/ExpirationDate';
import TrackerAffiliateLink from 'scenes/Gallery/components/TrackerAffiliateLink';
import TrackerLink from 'scenes/Gallery/components/TrackerLink';
import ConceptActions from 'scenes/Gallery/GalleryPage/Concepts/Actions';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';

import {
  DetailsColumn,
  PreviewColumn,
  StatusColumn,
  UploadedByColumn,
} from './Columns';
import ConceptsDataTableContext from './ConceptsDataTableContext';
import { BatchToolbarDataGrid } from './components/BatchToolbar';
import Toolbar from './components/Toolbar';
import {
  COLUMN_ID,
  COLUMN_PREVIEW,
  COLUMN_DETAILS,
  COLUMN_STATUS,
  COLUMN_START_DATE,
  COLUMN_EXPIRATION_DATE,
  COLUMN_COMMENTS,
  COLUMN_USED_IN_ADS,
  COLUMN_PROMO_CODE,
  COLUMN_TAGS,
  COLUMN_AI_TAGS,
  COLUMN_CONCEPT_FAMILIES,
  COLUMN_RATING,
  COLUMN_REQUESTED_BY,
  COLUMN_DESIGNER,
  COLUMN_SHARED_WITH,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COLUMN_UPDATED_DATE,
  COLUMN_EXCLUSIVE_FOR_AFFILIATES,
  COLUMN_USED_IN_AFFILIATES,
} from './constants';

const ConceptsDataTable = ({ height, tabs }) => {
  const {
    data,
    isLoading,
    isRefetching,
    tableSettings,
    columns,
    onReorder,
    onCollapse,
    onChangeSettings,
  } = useContext(ConceptsDataTableContext);

  const [screenTitle] = useScreenTitle();

  const queryClient = useQueryClient();

  const [childComponent, openChildComponent] = useChildComponent();

  const {
    isAllowRequestedBy,
    isAllowConceptFamilies,
    isAllowDesigner,
    isAllowSharedWith,
    isAllowUseInAffiliates,
    isAllowPromoCodes,
    isAllowExclusiveForAffiliates,
  } = useGalleryPermissions();

  const handleOpenConceptFamily = useCallback((id) => {
    openChildComponent(ConceptFamilyDetails, {
      id,
    });
  }, []);

  const handleOpenBP = useCallback(
    (id) => {
      openChildComponent(BusinessPartnerDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, BusinessPartnerDetails, queryClient]
  );

  const handleOpenPromoCode = (_, { id }) => {
    import('scenes/PromoCodes/components/PromoCodeDetails').then(
      (PromoCodeDetails) => {
        openChildComponent(PromoCodeDetails.default, {
          id,
          onChange: () => {
            queryClient.resetQueries({ active: true });
          },
        });
      }
    );
  };

  const handleOpenComments = useCallback(
    (id) =>
      openChildComponent(ConceptDetails, {
        activeTabId: TAB_COMMENTS,
        id,
      }),
    []
  );

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={onChangeSettings}
        onReorder={onReorder}
        onCollapse={onCollapse}
        tableSettings={tableSettings}
        columnsState={columns}
        checkboxSelection={true}
        toolbar={<Toolbar tabs={tabs} />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbarDataGrid
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
        storageKey={SCOPE_CONCEPTS}
        defaultColumnWidth={120}
        rowHeight={80}
        minHeight={height}
        notInitedLabel={'No records'}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_PREVIEW}
          headerName={'Preview'}
          width={130}
          fixed={true}
          resizable={false}
          sortable={false}
          formatter={(_, row) => <PreviewColumn row={row} />}
        />
        <DataGrid.Column
          field={COLUMN_DETAILS}
          headerName={'Details'}
          width={400}
          sortable={false}
          formatter={(_, row) => <DetailsColumn row={row} />}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          sortable={false}
          formatter={(value, row) => <StatusColumn value={value} row={row} />}
        />
        <DataGrid.Column
          field={COLUMN_START_DATE}
          headerName={'Start Date'}
          type={'date'}
          timeZone={'local'}
          template={'LL'}
        />
        <DataGrid.Column
          field={COLUMN_EXPIRATION_DATE}
          headerName={'Expiration Date'}
          width={130}
          sortable={false}
          formatter={(value) => (
            <ExpirationDate value={value} typographyVariant={'body2'} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_COMMENTS}
          headerName={'Comments'}
          width={90}
          sortable={false}
          formatter={(value, row) => {
            if (value) {
              return (
                <Link
                  onClick={() => handleOpenComments(row[CONCEPT.ID])}
                  underline={'none'}
                >
                  <AttributeItem
                    type={'label'}
                    value={value}
                    valueParser={'commentsCount'}
                    clickable={true}
                    color={'primary'}
                    typographyVariant={'caption'}
                    typographyColor={'primary'}
                  />
                </Link>
              );
            }
          }}
        />
        <DataGrid.Column
          field={COLUMN_USED_IN_ADS}
          headerName={'Used in Ads'}
          width={80}
          sortable={false}
          formatter={(value, row) => {
            if (value) {
              return (
                <TrackerLink dimension={DIMENSION_CONCEPT} id={row[CONCEPT.ID]}>
                  <AttributeItem
                    type={'label'}
                    valueParser={'ads'}
                    clickable={true}
                    underline={'always'}
                  />
                </TrackerLink>
              );
            }
          }}
        />
        <DataGrid.Column
          field={COLUMN_TAGS}
          headerName={'Internal Tags'}
          width={300}
          sortable={false}
          formatter={(_, row, { width }) => {
            const { [CONCEPT.INTERNAL_TAGS]: tags } = row;

            if (tags?.length) {
              return <Tags data={tags} width={width} lineClamp={2} />;
            }
          }}
        />
        <DataGrid.Column
          field={COLUMN_AI_TAGS}
          headerName={'AI Tags'}
          width={300}
          sortable={false}
          formatter={(value, _, { width }) => {
            if (value?.length) {
              return (
                <Tags
                  data={value.map(
                    ({ category, name }) => `${category}: ${name}`
                  )}
                  width={width}
                  lineClamp={2}
                />
              );
            }
          }}
        />
        {isAllowConceptFamilies ? (
          <DataGrid.Column
            field={COLUMN_CONCEPT_FAMILIES}
            headerName={'Concept Families'}
            limit={1}
            type={'list'}
            width={170}
            sortable={false}
            getItemValue={({ id, name }) => (
              <Link onClick={() => handleOpenConceptFamily(id)}>{name}</Link>
            )}
          />
        ) : null}
        {isAllowPromoCodes ? (
          <DataGrid.Column
            field={COLUMN_PROMO_CODE}
            headerName={'Promo Code'}
            type={'link'}
            width={170}
            sortable={false}
            onLinkClick={handleOpenPromoCode}
          />
        ) : null}
        <DataGrid.Column
          field={COLUMN_RATING}
          headerName={'Rating'}
          width={75}
          formatter={(value) => {
            if (value) {
              return (
                <AttributeItem
                  type={'label'}
                  value={value}
                  valueParser={'rating'}
                  typographyVariant={'body2'}
                />
              );
            }
          }}
        />
        {isAllowExclusiveForAffiliates ? (
          <DataGrid.Column
            field={COLUMN_EXCLUSIVE_FOR_AFFILIATES}
            headerName={'Exclusive for Affiliates'}
            type={'list'}
            unitsLabel={'Affiliates'}
            width={160}
            sortable={false}
            getItemValue={({ id, name }, _, row) => (
              <TrackerAffiliateLink
                id={id}
                dimension={DIMENSION_CONCEPT}
                drillDownId={row.id}
              >
                {name}
              </TrackerAffiliateLink>
            )}
          />
        ) : null}
        {isAllowUseInAffiliates ? (
          <DataGrid.Column
            field={COLUMN_USED_IN_AFFILIATES}
            headerName={'Used in Affiliates'}
            type={'list'}
            unitsLabel={'Affiliates'}
            width={150}
            sortable={false}
            getItemValue={({ id, name }, _, row) => (
              <TrackerAffiliateLink
                id={id}
                dimension={DIMENSION_CONCEPT}
                drillDownId={row.id}
              >
                {name}
              </TrackerAffiliateLink>
            )}
          />
        ) : null}
        {isAllowRequestedBy ? (
          <DataGrid.Column
            field={COLUMN_REQUESTED_BY}
            headerName={'Requested by'}
            width={150}
            formatter={(_, row) =>
              row[CONCEPT.JIRA_TASK] ? (
                <Link href={row[CONCEPT.JIRA_TASK]} target={'_blank'}>
                  {row[CONCEPT.REQUESTED_BY]}
                </Link>
              ) : (
                row[CONCEPT.REQUESTED_BY]
              )
            }
          />
        ) : null}
        {isAllowDesigner ? (
          <DataGrid.Column
            field={COLUMN_DESIGNER}
            headerName={'Original Creative Designer'}
            width={142}
            sortable={false}
            formatter={(_, row) => {
              if (row[CONCEPT.ORIGINAL][CREATIVE.OUTSOURCE_STUDIO]) {
                return `${
                  row[CONCEPT.ORIGINAL][CREATIVE.OUTSOURCE_STUDIO]?.name
                } (Outsource studio)`;
              } else if (row[CONCEPT.ORIGINAL][CREATIVE.DESIGNER]) {
                return row[CONCEPT.ORIGINAL][CREATIVE.DESIGNER];
              }
            }}
          />
        ) : null}
        {isAllowSharedWith ? (
          <DataGrid.Column
            field={COLUMN_SHARED_WITH}
            headerName={'Shared with'}
            type={'list'}
            unitsLabel={'Business Partners'}
            width={150}
            sortable={false}
            getItemValue={({ id, name }) => (
              <Link onClick={() => handleOpenBP(id)}>{name}</Link>
            )}
          />
        ) : null}
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Uploaded by'}
          width={150}
          formatter={(_, row) => (
            <UploadedByColumn onOpenBP={handleOpenBP} row={row} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Uploaded'}
          type={'date'}
          width={150}
          timeZone={'local'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          type={'date'}
          width={135}
          timeZone={'local'}
          template={'LLL'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          limit={3}
          actionsComponent={ConceptActions}
          onOpenChildComponent={openChildComponent}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};
export default ConceptsDataTable;
